import { useQuery } from '@tanstack/react-query'
import { Aviso, Botao, EstadoVazio, Selo } from '@via-tax/ui'
import { mesCurto, reaisCompacto } from '../formatos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * "Essa base é confiável?" (§6.5). Antes de qualquer simulação: buraco de mês é o
 * erro mais comum e o mais fatal, então ele vem primeiro e em forma, não em número.
 */
export function QualidadeBase({ irParaEnvio }: { irParaEnvio: () => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.documento.qualidade.queryOptions())

  if (q.isPending) return <p className="text-sm text-tinta-3">Carregando…</p>
  if (q.isError) return <Aviso>{mensagemDeErro(q.error)}</Aviso>
  const d = q.data
  if (d.documentos.total === 0) {
    return (
      <EstadoVazio titulo="Sem documentos para avaliar" acao={<Botao variante="primario" onClick={irParaEnvio}>Enviar XMLs</Botao>}>
        A qualidade da base aparece depois do primeiro envio: meses cobertos, entradas × saídas e problemas de cadastro nos itens.
      </EstadoVazio>
    )
  }

  // Todas as competências do intervalo, inclusive as vazias.
  const meses: string[] = []
  if (d.primeiraCompetencia && d.ultimaCompetencia) {
    let [a, m] = d.primeiraCompetencia.split('-').map(Number) as [number, number]
    for (let i = 0; i < 240; i++) {
      const c = `${a}-${String(m).padStart(2, '0')}`
      if (c > d.ultimaCompetencia) break
      meses.push(c)
      if (++m > 12) {
        m = 1
        a++
      }
    }
  }
  const valor = (c: string, papel: 'ENTRADA' | 'SAIDA') => d.competencias.find((x) => x.competencia === c && x.papel === papel)?.valor ?? 0
  const qtd = (c: string) => d.competencias.filter((x) => x.competencia === c).reduce((s, x) => s + x.documentos, 0)
  const maior = Math.max(1, ...meses.flatMap((c) => [valor(c, 'ENTRADA'), valor(c, 'SAIDA')]))
  const cobertura = meses.length ? Math.round(((meses.length - d.mesesFaltando.length) / meses.length) * 100) : 0
  const pct = (n: number, t: number) => (t ? `${((n / t) * 100).toFixed(1).replace('.', ',')}%` : '—')
  const itens = d.itensMercadoria

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Qualidade da base</h1>
          <p className="mt-1 max-w-prose text-sm text-tinta-2">
            A simulação só é tão boa quanto os documentos. Meses faltando ou cadastro de produto ruim viram número errado
            no relatório — e o relatório carimba isso.
          </p>
        </div>
        {d.mesesFaltando.length === 0 && meses.length >= 12 ? <Selo tom="ok">Base contínua</Selo> : <Selo tom="alerta">Base incompleta</Selo>}
      </header>

      <section className="rounded-lg border border-linha bg-painel">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-linha px-5 py-3">
          <h2 className="text-sm font-semibold">Cobertura por competência</h2>
          <p className="text-xs text-tinta-3">
            <span className="num">{meses.length - d.mesesFaltando.length}</span> de <span className="num">{meses.length}</span> meses com documento ({cobertura}%)
            {meses.length < 12 && ' · menos de 12 meses: o anual será extrapolado'}
          </p>
        </div>
        <div className="overflow-x-auto px-5 py-4">
          <div className="flex min-w-max items-end gap-1.5" role="img" aria-label="Valor de entradas e saídas por mês">
            {meses.map((c) => {
              const falta = d.mesesFaltando.includes(c)
              const e = valor(c, 'ENTRADA')
              const s = valor(c, 'SAIDA')
              return (
                <div key={c} className="flex w-12 flex-col items-center gap-1" title={falta ? `${c}: sem documento` : `${c}: ${qtd(c)} documento(s)`}>
                  <div className={`flex h-28 w-full items-end justify-center gap-0.5 rounded-sm ${falta ? 'border border-dashed border-alerta/60 bg-alerta/5' : ''}`}>
                    {!falta && (
                      <>
                        <span className="w-3.5 rounded-t-sm bg-navy/35" style={{ height: `${Math.max(2, (e / maior) * 100)}%` }} />
                        <span className="w-3.5 rounded-t-sm bg-navy" style={{ height: `${Math.max(2, (s / maior) * 100)}%` }} />
                      </>
                    )}
                  </div>
                  <span className={`num text-[10px] ${falta ? 'font-semibold text-alerta' : 'text-tinta-3'}`}>{mesCurto(c)}</span>
                </div>
              )
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-tinta-3">
            <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-navy/35" />Entradas</span>
            <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-navy" />Saídas</span>
            <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-sm border border-dashed border-alerta" />Mês sem documento</span>
            <span className="ml-auto">escala: maior mês = {reaisCompacto(maior)}</span>
          </div>
          {d.mesesFaltando.length > 0 && (
            <div className="mt-3">
              <Aviso>
                Faltam documentos de {d.mesesFaltando.map(mesCurto).join(', ')}. Envie esses meses antes de simular — um mês sem
                nota parece um mês sem imposto.
              </Aviso>
            </div>
          )}
        </div>
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="rounded-lg border border-linha bg-painel">
          <h2 className="border-b border-linha px-5 py-3 text-sm font-semibold">Documentos</h2>
          <dl className="divide-y divide-linha text-[13px]">
            <Linha rotulo="Total" valor={d.documentos.total.toLocaleString('pt-BR')} />
            <Linha rotulo="Cancelados" valor={d.documentos.cancelados.toLocaleString('pt-BR')} nota="ficam na base, fora da simulação" />
            <Linha rotulo="Denegados" valor={d.documentos.denegados.toLocaleString('pt-BR')} />
            <Linha
              rotulo="Sem protocolo de autorização"
              valor={d.documentos.semProtocolo.toLocaleString('pt-BR')}
              alerta={d.documentos.semProtocolo > 0}
              nota="XML sem o retorno da SEFAZ — pode nunca ter sido autorizado"
            />
            <Linha rotulo="Transferências entre unidades" valor={d.documentos.internos.toLocaleString('pt-BR')} nota="não entram como compra nem venda" />
            <Linha rotulo="De outra empresa (separados)" valor={d.documentos.deTerceiros.toLocaleString('pt-BR')} alerta={d.documentos.deTerceiros > 0} />
            <Linha rotulo="Já no leiaute da Reforma (IBS/CBS)" valor={pct(d.documentos.comIbsCbs, d.documentos.total)} />
          </dl>
        </section>

        <section className="rounded-lg border border-linha bg-painel">
          <h2 className="border-b border-linha px-5 py-3 text-sm font-semibold">Itens de mercadoria (NF-e e NFC-e)</h2>
          <dl className="divide-y divide-linha text-[13px]">
            <Linha rotulo="Itens" valor={itens.total.toLocaleString('pt-BR')} />
            <Linha rotulo="Sem NCM" valor={`${itens.semNcm.toLocaleString('pt-BR')} (${pct(itens.semNcm, itens.total)})`} alerta={itens.semNcm > 0} />
            <Linha rotulo="NCM fora do formato" valor={`${itens.ncmInvalido.toLocaleString('pt-BR')} (${pct(itens.ncmInvalido, itens.total)})`} alerta={itens.ncmInvalido > 0} />
            <Linha
              rotulo="NCM genérico (00000000)"
              valor={`${itens.ncmGenerico.toLocaleString('pt-BR')} (${pct(itens.ncmGenerico, itens.total)})`}
              alerta={itens.ncmGenerico > 0}
              nota="sem NCM real não há como classificar IBS/CBS"
            />
          </dl>
          <p className="border-t border-linha px-5 py-3 text-xs text-tinta-3">
            A conferência contra a tabela TIPI vigente e a classificação de cClassTrib chegam na Revisão de Estoque (F3).
          </p>
        </section>
      </div>
    </div>
  )
}

function Linha({ rotulo, valor, nota, alerta }: { rotulo: string; valor: string; nota?: string; alerta?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-2.5">
      <dt>
        {rotulo}
        {nota && <span className="block text-[11px] text-tinta-3">{nota}</span>}
      </dt>
      <dd className={`num shrink-0 ${alerta ? 'font-medium text-alerta' : ''}`}>{valor}</dd>
    </div>
  )
}
